/**
 * Асинхронный поиск пользователей с debounce. Использует общий Input.
 * Минимум 2 символа, debounce 300 мс.
 *
 * С `roster` — режим короткого списка: варианты уже на руках (участники
 * группы), показываются по фокусу без ввода и фильтруются локально. Сеть не
 * трогается вовсе.
 *
 * С `onCreate` последним пунктом списка стоит «Добавить «запрос»» — для
 * человека, которого в базе нет. Пункт, а не кнопка под полем: найти и
 * вписать — один жест, и человек видит, что поиск уже ничего не дал.
 *
 * @use выбор человека, когда список большой и грузится с сервера — поиск от 2 символов с debounce; с `roster` — выбор из известного короткого списка по фокусу; с `onCreate` — пункт «Добавить «…»» для человека не из базы.
 */

import { useState, useRef, useEffect, useCallback, useLayoutEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Plus } from 'lucide-react';
import { usersApi, type UserSearchResult } from '@/shared/api';
import { Input } from '../Input';
import { cn } from '@/shared/lib/cn';
import { useDebouncedValue } from '@/shared/lib/useDebouncedValue';
import { Scroller } from '@/shared/ui/Scroller';
import styles from './AsyncUserSelect.module.css';

export interface AsyncUserSelectProps {
  placeholder?: string;
  onSelect: (user: UserSearchResult) => void;
  disabled?: boolean;
  syncFromKeycloak?: boolean;
  staffOnly?: boolean;
  excludeUserIds?: string[];
  excludeReason?: string;
  roster?: UserSearchResult[];
  rosterEmptyText?: string;
  autoFocus?: boolean;
  onCreate?: (text: string) => void;
  createHint?: string;
}

export function AsyncUserSelect({
  placeholder = 'Начните вводить имя или email…',
  onSelect,
  disabled = false,
  syncFromKeycloak = false,
  staffOnly = false,
  excludeUserIds,
  excludeReason = 'выбрать нельзя',
  roster,
  rosterEmptyText = 'В списке никого нет',
  autoFocus = false,
  onCreate,
  createHint = 'нет в базе',
}: AsyncUserSelectProps) {
  const excluded = useMemo(() => excludeUserIds ?? [], [excludeUserIds?.join(',')]); // eslint-disable-line react-hooks/exhaustive-deps
  const [query, setQuery] = useState('');
  const [options, setOptions] = useState<UserSearchResult[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLElement>(null);
  const [dropdownRect, setDropdownRect] = useState<{ top: number; left: number; width: number } | null>(null);
  const debouncedQuery = useDebouncedValue(query, 300);
  const searchSeq = useRef(0);

  const updateDropdownRect = useCallback(() => {
    const el = wrapperRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const next = { top: rect.bottom + 4, left: rect.left, width: rect.width };
    setDropdownRect((prev) =>
      prev && prev.top === next.top && prev.left === next.left && prev.width === next.width
        ? prev
        : next,
    );
  }, []);

  useLayoutEffect(() => {
    if (!isOpen) return;
    let frame = 0;
    const tick = () => {
      updateDropdownRect();
      frame = requestAnimationFrame(tick);
    };
    tick();
    return () => cancelAnimationFrame(frame);
  }, [isOpen, updateDropdownRect]);

  const search = useCallback(
    async (q: string) => {
      const seq = ++searchSeq.current;
      if (roster) {
        const needle = q.trim().toLowerCase();
        setOptions(
          needle
            ? roster.filter((u) =>
                [u.displayName, u.username ?? '', u.email ?? '', u.department ?? ''].some((v) =>
                  v.toLowerCase().includes(needle),
                ),
              )
            : roster,
        );
        setHighlightedIndex(-1);
        return;
      }
      if (q.length < 2) {
        setOptions([]);
        setIsOpen(false);
        setIsLoading(false);
        return;
      }
      setIsLoading(true);
      try {
        const res = await usersApi.search(q, syncFromKeycloak, staffOnly);
        if (seq !== searchSeq.current) return;
        const data = Array.isArray(res.data) ? res.data : [];
        setOptions(data);
        setIsOpen(true);
        setHighlightedIndex(-1);
      } catch {
        if (seq === searchSeq.current) setOptions([]);
      } finally {
        if (seq === searchSeq.current) setIsLoading(false);
      }
    },
    [syncFromKeycloak, staffOnly, roster],
  );

  const isExcluded = useCallback((user: UserSearchResult) => excluded.includes(user.id), [excluded]);

  const createText = query.trim().replace(/\s+/g, ' ');
  const canCreate = onCreate != null && createText.length >= 2;
  const createIndex = options.length;
  const itemCount = options.length + (canCreate ? 1 : 0);

  const handleCreate = () => {
    if (!canCreate) return;
    searchSeq.current += 1;
    setIsLoading(false);
    onCreate(createText);
    setQuery('');
    setOptions([]);
    setIsOpen(false);
    inputRef.current?.blur();
  };

  const handleSelect = (user: UserSearchResult) => {
    if (isExcluded(user)) return;
    searchSeq.current += 1;
    setIsLoading(false);
    onSelect(user);
    setQuery('');
    setOptions([]);
    setIsOpen(false);
    inputRef.current?.blur();
  };

  const nextSelectable = (from: number, step: 1 | -1): number => {
    for (let i = 1; i <= itemCount; i += 1) {
      const idx = (from + step * i + itemCount * i) % itemCount;
      if (idx === createIndex || !isExcluded(options[idx])) return idx;
    }
    return -1;
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen || itemCount === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex((prev) => nextSelectable(prev, 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex((prev) => nextSelectable(prev < 0 ? 0 : prev, -1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (canCreate && (highlightedIndex === createIndex || (highlightedIndex < 0 && options.length === 0))) {
        handleCreate();
      }
      else if (highlightedIndex >= 0) handleSelect(options[highlightedIndex]);
    } else if (e.key === 'Escape') {
      setIsOpen(false);
      setHighlightedIndex(-1);
    }
  };

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(target) &&
        !(dropdownRef.current && dropdownRef.current.contains(target))
      ) {
        setIsOpen(false);
        setHighlightedIndex(-1);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  useEffect(() => {
    if (!autoFocus) return;
    let inner = 0;
    const outer = requestAnimationFrame(() => {
      inner = requestAnimationFrame(() => inputRef.current?.focus({ preventScroll: true }));
    });
    return () => {
      cancelAnimationFrame(outer);
      cancelAnimationFrame(inner);
    };
  }, [autoFocus]);

  useEffect(() => {
    void search(debouncedQuery);
  }, [debouncedQuery, search]);

  return (
    <div ref={wrapperRef} className={styles.wrapper}>
      <Input
        ref={inputRef}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onKeyDown={handleKeyDown}
        onFocus={() => {
          if (roster) {
            void search(query);
            setIsOpen(true);
            return;
          }
          if (query.length >= 2 && options.length > 0) setIsOpen(true);
        }}
        placeholder={placeholder}
        disabled={disabled}
        autoComplete="off"
      />
      {isOpen &&
        dropdownRect != null &&
        createPortal(
          <Scroller
            ref={dropdownRef}
            className={styles.dropdown}
            style={{ top: dropdownRect.top, left: dropdownRect.left, width: dropdownRect.width }}
            onPointerDown={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
          >
          {isLoading ? (
            <div className={styles.empty}>Поиск…</div>
          ) : options.length === 0 ? (
            <div className={styles.empty}>{roster ? rosterEmptyText : 'Пользователи не найдены'}</div>
          ) : (
            options.map((user, index) => {
              const disabled = isExcluded(user);
              return (
                <div
                  key={user.id}
                  className={cn(
                    styles.option,
                    disabled && styles.optionDisabled,
                    !disabled && index === highlightedIndex && styles.highlighted,
                  )}
                  aria-disabled={disabled || undefined}
                  onClick={() => handleSelect(user)}
                  onMouseEnter={() => !disabled && setHighlightedIndex(index)}
                >
                  <div className={styles.optionName}>
                    {user.displayName}
                    {disabled && <span className={styles.optionReason}>{excludeReason}</span>}
                  </div>
                  {(user.department || user.username || user.email) && (
                    <div className={styles.optionMeta}>
                      {[user.department, user.username && `@${user.username}`, user.email]
                        .filter(Boolean)
                        .join(' · ')}
                    </div>
                  )}
                </div>
              );
            })
          )}
          {canCreate && (
            <div
              className={cn(styles.option, styles.create, highlightedIndex === createIndex && styles.highlighted)}
              onClick={handleCreate}
              onMouseEnter={() => setHighlightedIndex(createIndex)}
            >
              <Plus size={16} aria-hidden className={styles.createIcon} />
              <span className={styles.optionName}>
                Добавить «{createText}»
                <span className={styles.optionReason}>{createHint}</span>
              </span>
            </div>
          )}
          </Scroller>,
          document.body,
        )}
    </div>
  );
}
